import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from './shared/components'
import { ProtectedRoute } from '@trafiflow/shared-ui'
import { ROUTES } from './shared/config'

// Auth Pages
import Login from './shared/pages/Login'
import Signup from './shared/pages/Signup'
import Profile from './shared/pages/Profile'
import Home from './shared/pages/Home'

// Store Module
import { StoreHome, ProductList, ProductDetail, Cart, Checkout } from './modules/store'

// Agents Module
import { AgentList, AgentDetail, Conversations, MyAgents, AgentMarket, AgentBuilder } from './modules/agents'

// Marketing Module
import { Promotions, SummaryPage, AdGeneratorPage, CompetitorAnalysisPage, TikTokIntelPage } from './modules/marketing'

// Budget Allocation Module
import { BudgetAllocationPage } from './modules/budget-allocation'

// Creatives Module
import { CreativesPage, ProductAnalysisPage } from './modules/creatives'

// Settings Module
import { MetaConfigPage } from './modules/settings'
import {
  Ads,
  SavedAds,
  Brands,
  Swipe,
  Storyboard,
  StoryboardDetail,
  CreatorEvaluator,
  MarketingAgentsChat,
  StoryboardReplacementEngine,
  FootageRepository,
  CreatorRepository,
  QueueDebug,
  VideoStatusDebug,
  BrandContextEngineering,
  PromptEngineeringPage,
} from './modules/marketing'

// Storyboard Generator Pages
import { StoryboardGenerator } from './pages/storyboard-generator'
import { AdRemixPage } from './pages/ad-remix'
import { MarketingLayout } from './modules/marketing/components'
import { MarketingOnboardingPage } from './modules/marketing/pages/onboarding'
import { OnboardingProvider } from './modules/marketing/contexts/OnboardingContext'

// Admin Module
import { Roles, People, OrgChart } from './modules/admin'

// Billing Module
import { BillingHome } from './modules/billing'

// My Company Module
import { MyCompanyHome } from './modules/my-company'

// Stores Module (Next Stores)
import { StoresHome } from './modules/stores'

// Funnels Module
import { Dashboard, FunnelsList, FunnelDetail, UpsellBuilder, OffersList, Changelog, Alerts, AlertRules, AnalyticsPage } from './modules/funnels'
import { FunnelRuntimeProvider } from './modules/funnels/runtime'

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route path={ROUTES.SIGNUP} element={<Signup />} />

      {/* Protected Routes */}
      <Route
        path={ROUTES.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <Home />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Store Module Routes */}
      <Route
        path={ROUTES.STORE.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <StoreHome />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.STORE.PRODUCTS}
        element={
          <ProtectedRoute>
            <Layout>
              <ProductList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/store/products/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.STORE.CART}
        element={
          <ProtectedRoute>
            <Layout>
              <Cart />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.STORE.CHECKOUT}
        element={
          <ProtectedRoute>
            <Layout>
              <Checkout />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Analytics Summary Module Route */}
      <Route
        path={ROUTES.ANALYTICS_SUMMARY}
        element={
          <ProtectedRoute>
            <Layout>
              <SummaryPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Budget Allocation Module Route */}
      <Route
        path={ROUTES.BUDGET_ALLOCATION}
        element={
          <ProtectedRoute>
            <Layout>
              <BudgetAllocationPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Creatives Module Routes */}
      <Route
        path={ROUTES.CREATIVES}
        element={
          <ProtectedRoute>
            <Layout>
              <CreativesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.CREATIVES_ANALYSIS}
        element={
          <ProtectedRoute>
            <Layout>
              <ProductAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings Module Routes */}
      <Route
        path={ROUTES.SETTINGS.META_CONFIG}
        element={
          <ProtectedRoute>
            <Layout>
              <MetaConfigPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Agents Module Routes */}
      <Route
        path={ROUTES.AGENTS.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <MyAgents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENTS.MY_AGENTS}
        element={
          <ProtectedRoute>
            <Layout>
              <MyAgents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENTS.MARKET}
        element={
          <ProtectedRoute>
            <Layout>
              <AgentMarket />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENTS.BUILDER}
        element={
          <ProtectedRoute>
            <Layout>
              <AgentBuilder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENTS.LIST}
        element={
          <ProtectedRoute>
            <Layout>
              <AgentList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <AgentDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENTS.CONVERSATIONS}
        element={
          <ProtectedRoute>
            <Layout>
              <Conversations />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Marketing Onboarding Route */}
      <Route
        path={ROUTES.MARKETING.ONBOARDING}
        element={
          <ProtectedRoute>
            <OnboardingProvider>
              <MarketingOnboardingPage />
            </OnboardingProvider>
          </ProtectedRoute>
        }
      />

      {/* Marketing Module Routes - Nested under shared MarketingLayout */}
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <MarketingLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/marketing/ad-generator" replace />} />
        <Route path="ad-generator" element={<AdGeneratorPage />} />
        <Route path="competitor-analysis" element={<CompetitorAnalysisPage />} />
        <Route path="tiktok-intel" element={<TikTokIntelPage />} />
        <Route path="summary" element={<SummaryPage />} />
        <Route path="promotions" element={<Promotions />} />

        {/* Research */}
        <Route path="research/ads" element={<Ads />} />
        <Route path="research/saved-ads" element={<SavedAds />} />
        <Route path="research/brands" element={<Brands />} />

        {/* AI Playground */}
        <Route path="playground/swipe" element={<Swipe />} />
        <Route path="playground/storyboard" element={<Storyboard />} />
        <Route path="playground/storyboard/:storyboardId" element={<StoryboardDetail />} />
        <Route path="playground/creator-evaluator" element={<CreatorEvaluator />} />

        {/* AI Storyboard Generator */}
        <Route path="playground/storyboard-generator" element={<StoryboardGenerator />} />

        {/* Ad Remix Engine */}
        <Route path="playground/ad-remix" element={<AdRemixPage />} />

        {/* AI Chats */}
        <Route path="chats" element={<MarketingAgentsChat />} />
        <Route path="chats/storyboard-replacement" element={<StoryboardReplacementEngine />} />

        {/* Repository */}
        <Route path="repository/footage" element={<FootageRepository />} />
        <Route path="repository/creators" element={<CreatorRepository />} />

        {/* Debug */}
        <Route path="debug/queue" element={<QueueDebug />} />
        <Route path="debug/videos" element={<VideoStatusDebug />} />
        <Route path="debug/brand-context" element={<BrandContextEngineering />} />
        <Route path="debug/prompt-engineering" element={<PromptEngineeringPage />} />
      </Route>

      {/* Admin Module Routes */}
      <Route
        path={ROUTES.ADMIN.ORGANIZATION.ROLES}
        element={
          <ProtectedRoute>
            <Layout>
              <Roles />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN.ORGANIZATION.PEOPLE}
        element={
          <ProtectedRoute>
            <Layout>
              <People />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN.ORGANIZATION.ORGCHART}
        element={
          <ProtectedRoute>
            <Layout>
              <OrgChart />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Billing Module Routes */}
      <Route
        path={ROUTES.BILLING.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <BillingHome />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* My Company Module Routes */}
      <Route
        path={ROUTES.MY_COMPANY.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <MyCompanyHome />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Stores Module Routes (Next Stores) */}
      <Route
        path={ROUTES.STORES.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <StoresHome />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Funnels Module Routes */}
      <Route
        path={ROUTES.FUNNELS.DASHBOARD}
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.FUNNELS.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <FunnelsList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/funnels/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <FunnelRuntimeProvider>
                <FunnelDetail />
              </FunnelRuntimeProvider>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/funnels/:id/steps/:stepId/builder"
        element={
          <ProtectedRoute>
            <Layout>
              <UpsellBuilder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.FUNNELS.ANALYTICS}
        element={
          <ProtectedRoute>
            <Layout>
              <AnalyticsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.FUNNELS.OFFERS}
        element={
          <ProtectedRoute>
            <Layout>
              <OffersList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.FUNNELS.CHANGELOG}
        element={
          <ProtectedRoute>
            <Layout>
              <Changelog />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.FUNNELS.ALERTS.HOME}
        element={
          <ProtectedRoute>
            <Layout>
              <Alerts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.FUNNELS.ALERTS.RULES}
        element={
          <ProtectedRoute>
            <Layout>
              <AlertRules />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all route - redirect to home if authenticated, login if not */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Layout>
              <Home />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default App
